'use client';
import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

/**
 * 배너 슬라이더 — 가이드 benner-guide.html 의 `bn-slider-unit` 규약을 React 로 옮겼다.
 * 한 장이면 그대로 보여 주고(버튼·도트 없음 — 가이드 동작), 여러 장이면
 * 좌우 화살표(bn-arr)·도트(bn-dots)·5초 자동 넘김(data-auto)을 단다.
 */
export function BnSlider({ children, auto = true, className = '' }: {
  children: React.ReactNode[];
  auto?: boolean;
  className?: string;
}) {
  const total = children.length;
  const [cur, setCur] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!auto || total <= 1) return;
    // 움직임 줄이기(OS 설정)를 켠 사용자에게는 자동 넘김을 멈춘다(UX 채택안 #7)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    timer.current = setInterval(() => setCur((c) => (c + 1) % total), 5000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [auto, total, cur]); // cur 의존 — 손으로 넘기면 타이머가 다시 돈다(가이드 resetAuto)

  if (total === 0) return null;
  return (
    <div className={`bn-slider-unit ${className}`.trim()}>
      <div className="bn-track" style={{ transform: `translateX(-${cur * 100}%)` }}>
        {children.map((c, i) => <div className="bn-slide" key={i}>{c}</div>)}
      </div>
      {total > 1 && (
        <>
          <button className="bn-arr bn-arr-prev" onClick={() => setCur((cur - 1 + total) % total)} aria-label="prev">
            <Icon name="chevron-left" />
          </button>
          <button className="bn-arr bn-arr-next" onClick={() => setCur((cur + 1) % total)} aria-label="next">
            <Icon name="chevron-right" />
          </button>
          <div className="bn-dots">
            {children.map((_, i) => (
              <button key={i} className={`bn-dot${i === cur ? ' on' : ''}`} onClick={() => setCur(i)} aria-label={`slide ${i + 1}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
